import React from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import styled, { withTheme } from 'styled-components';
import { readableColor, getContrast, mix } from 'polished';
import breakpoint from 'styled-components-breakpoint';
import { Link } from 'react-router-dom';
import { G90Navigation } from '@goodwaygroup/ui-components';
import { UNIFIED_NAV_VIEW } from 'constants/capabilities';
import SidebarHamburger from 'components/atoms/layout/SidebarHamburger';
import { getLogoSettings } from 'helpers/styledComponentHelpers';

const HeaderWrapper = styled.div`
    display: flex;
    flex-direction: row;
    position: fixed;
    z-index: 10000;
    top: 0;
    padding-right: 20px;
    width: 100%;
    background-color: ${props => props.theme.global.colors.headerBarColor};
    color: #fff;
    align-items: center;
    height: 70px;
    font-size: 16px;
    border-bottom: ${props => (getContrast('#fff', props.theme.global.colors.headerBarColor) < props.theme.global.constrastThresholdsFromWhite.headerBarBorder ? props.theme.global.borders.layout : 'none')};

    ${breakpoint('xs', 'md')`
        font-size: 12px;
        height: 60px;
    `}
`;

const NavUl = styled.ul`
    margin: 0;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;

    li {
        display: inline-block;
        margin-right: 15px;
        user-select: none;
    }

    li:last-child {
        margin-right: 0px;
    }
`;

const MainLogo = styled.div`
    width: ${props => getLogoSettings(props).width}px;
    height: ${props => getLogoSettings(props).height}px;
    background-image: url(${props => props.theme.global.images[getLogoSettings(props).image]});
    background-size: contain;
    background-repeat: no-repeat;
    cursor: pointer;

    ${breakpoint('xs', 'md')`
        background-size: cover;
        width: 34px;
        height: 40px
    `}
`;

const MainLogoWrapper = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: ${props => getLogoSettings(props).backgroundColor || '#000'};
    height: inherit;
    width: 200px;

    ${breakpoint('xs', 'md')`
        width: 65px;
        min-width: 60px;
    `}
`;

const SecondaryLogoWrapper = styled.div`
    display: flex;
    margin-left: 20px;
    justify-content: flex-start;
    align-items: center;
    cursor: pointer;
`;

const BrandLogo = styled.img`
    display: block;
    max-width:230px;
    max-height:52px;
    width: auto;
    height: auto;

    ${breakpoint('xs', 'md')`
        max-width: 115px;
        max-height: 40px;
    `}
`;

const EntityLogo = styled(BrandLogo)`

`;

const NavWrapper = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    margin-left: auto;
    width: 300px;
`;

const LogoDivider = styled.div`
    width: 1px;
    height: 60px;
    margin: 0px 20px;
    background-color: ${props => props.theme.global.colors.headerBarColor && readableColor(props.theme.global.colors.headerBarColor)};

    ${breakpoint('xs', 'md')`
        height: 40px;
    `}
`;

const NavLink = styled(Link)`
    color: ${props => readableColor(props.theme.global.colors.headerBarColor)};

    &:hover, &:active {
        text-decoration: none;
        color: ${props => props.theme.colors.lochmara};
    }
`;

const EnrollmentNavLink = styled(NavLink)`
    ${breakpoint('xs', 'md')`
        display: none;
    `}
`;

const generateLogos = (props) => {
    const hasBrandLogo = props.brandBranding ? !!props.brandBranding.logoUrl : false;
    const hasEntityLogo = props.entityBranding ? !!props.entityBranding.logoUrl : false;
    const hasBothLogos = hasBrandLogo && hasEntityLogo;
    const hasEitherLogo = hasBrandLogo || hasEntityLogo;

    if (hasEitherLogo) {
        return (
            <SecondaryLogoWrapper onClick={props.homeClick}>
                {hasBrandLogo && <BrandLogo src={props.brandBranding && props.brandBranding.logoUrl} />}
                {hasBothLogos && <LogoDivider />}
                {hasEntityLogo && <EntityLogo src={props.entityBranding && props.entityBranding.logoUrl} />}
            </SecondaryLogoWrapper>
        );
    }

    return null;
};

const ShowMainLogo = (props) => {
    if (get(props.theme, 'whitelabel.showGGBranding', false)) {
        return (
            <MainLogoWrapper>
                <MainLogo onClick={() => props.homeClick()} />
            </MainLogoWrapper>
        );
    }

    return null;
};

const Header = (props) => {
    const headerBarColor = props.theme.global.colors.headerBarColor;
    const doShowG90Header = !!props.currentUserScope && props.currentUserScope
        .includes(UNIFIED_NAV_VIEW);
    return (
        <HeaderWrapper className="hidden-print">
            <ShowMainLogo {...props} />
            {generateLogos(props)}
            <NavWrapper>
                {
                    <NavUl>
                        {!(props.hideLinks || doShowG90Header) && (
                            <li><NavLink key={0} target="_blank" to={'/glossary'}>GLOSSARY</NavLink></li>
                        )}
                        {props.enrollmentSite && (
                            <li><EnrollmentNavLink key={0} target="_self" to={`${props.enrollmentSite}`}>ENROLLMENT</EnrollmentNavLink></li>
                        )}
                        {!doShowG90Header && props.isOkta && (
                            <li><NavLink key={0} target="_self" to={'/admin'}>ADMIN</NavLink></li>
                        )}
                        {!(props.hideLinks || props.hideHamburgerMenu) &&
                            <li><SidebarHamburger admin={props.admin} parsedUrl={props.parsedUrl} onClick={() =>
                                props.toggleMobileNav()} mobileNavExpanded={props.mobileNavExpanded} /></li>
                        }
                    </NavUl>
                }
            </NavWrapper>
            {
                doShowG90Header &&
                <G90Navigation
                    doLogout={props.doLogout}
                    name={props.currentUserName}
                    email={props.currentUserEmail}
                    foregroundColor={readableColor(headerBarColor)}
                    hoverColor={mix(0.5, headerBarColor, readableColor(headerBarColor))}
                    {...props}
                    borderColor={props.borderColor}
                    homePath='/'
                    dashboardsPath='/dashboard'
                    adminPath='/admin'
                />
            }
        </HeaderWrapper>
    );
};

Header.propTypes = {
    mobileNavExpanded: PropTypes.bool,
    hideLinks: PropTypes.bool,
    hideHamburgerMenu: PropTypes.bool,
    toggleMobileNav: PropTypes.func,
    homeClick: PropTypes.func,
    contactEmail: PropTypes.string,
    entityName: PropTypes.string,
    borderColor: PropTypes.string
};

export default withTheme(Header);



// WEBPACK FOOTER //
// ./src/components/organisms/Header.js